import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Divider, Header, List, Tab } from 'semantic-ui-react'
import { serverUrl } from '../../../../utilites/config'
import { reconnectingSocket } from '../../../../utilites/socket'
import DataStatusIndicator from '../SolarFluxLive/components/DataStatusIndicator'
import Linechart from '../SolarFluxLive/components/Linechart'
import { useTechnicalDay } from '../SolarFluxLive/hooks/useTechnicalDay'
import './SolarIndices.scss'

const formatUtcForFilename = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}_${pad(d.getUTCDate())}-${pad(
    d.getUTCMonth() + 1,
  )}-${d.getUTCFullYear()}`
}

function SolarIndices() {
  const connectionRef = useRef<any>(null)
  const isTechnicalDay = useTechnicalDay()

  // Jeden wspólny przełącznik widoku (Hourly/Daily) dla obu zakładek
  const [isHourly, setIsHourly] = useState(true)

  // Dane godzinowe i dzienne (wspólne bufory, zależne od aktywnej zakładki)
  const [dataXHour, setDataXHour] = useState<Date[]>([])
  const [dataYHour, setDataYHour] = useState<number[]>([])
  const [dataXDaily, setDataXDaily] = useState<Date[]>([])
  const [dataYDaily, setDataYDaily] = useState<number[]>([])

  // 0 = F10.7, 1 = F30
  const [activeTab, setActiveTab] = useState(0)
  const bandLabel = activeTab === 0 ? 'F10.7' : 'F30'
  const downloadFilename = `[FLUX][${bandLabel}][${isHourly ? '1h' : '1d'}]_${formatUtcForFilename()}`

  // Odbiór strumienia (ostatnia próbka do serii godzinowej)
  const saveSocketData = useCallback((d: any) => {
    const arr = JSON.parse(d.body)
    const last = arr[arr.length - 1]
    // ostatni znacznik czasu + 60 s (spójne z backendowym próbkowaniem minutowym)
    const t = new Date(new Date(last.date).getTime() + 60 * 1000)
    const oneHourMs = 60 * 60 * 1000

    setDataXHour(prev => {
      const shouldSlice = prev.length > 0 && t.getTime() - prev[0].getTime() > oneHourMs
      return shouldSlice ? [...prev.slice(1), t] : [...prev, t]
    })
    setDataYHour(prev => {
      // Sync slice logic with dataXHour - if we have more than ~60 points (1 hour), slice
      const shouldSlice = prev.length >= 60
      return shouldSlice ? [...prev.slice(1), parseFloat(last.value)] : [...prev, parseFloat(last.value)]
    })
  }, [])

  // Pobranie danych dziennych
  useEffect(() => {
    const url = `${serverUrl}/flux/historicalDailyData${activeTab === 0 ? '' : '?band=30'}`
    axios.get(url).then(({ data }) => {
      const X: Date[] = []
      const Y: number[] = []
      data.forEach((item: any) => {
        if (item.value > 0) {
          Y.push(item.value)
          X.push(new Date(item.date))
        }
      })
      setDataXDaily(X)
      setDataYDaily(Y)
    })
  }, [activeTab])

  // Pobranie danych godzinowych + subskrypcja websocket
  useEffect(() => {
    const url = `${serverUrl}/flux/historicalHourlyData${activeTab === 0 ? '' : '?band=30'}`
    axios.get(url).then(({ data }) => {
      const X: Date[] = []
      const Y: number[] = []
      data.forEach((item: any) => {
        Y.push(item.value)
        X.push(new Date(item.date))
      })
      setDataXHour(X)
      setDataYHour(Y)
    })

    // WebSocket: /topic/flux-hour lub /topic/flux-hour-30
    connectionRef.current = reconnectingSocket(
      `${serverUrl}/websocket`,
      saveSocketData,
      `/topic/flux-hour${activeTab === 0 ? '' : '-30'}`,
      window.location.href,
    )

    return () => {
      const ref = connectionRef.current
      if (!ref) return
      ref.disconnect?.()
      connectionRef.current = null
    }
  }, [activeTab, saveSocketData])

  const panes = [
    {
      menuItem: 'F10.7',
      render: () => (
        <div>
          {isHourly ? (
            <Linechart
              key='Linechart-f107-hour'
              data={[
                {
                  x: dataXHour,
                  y: dataYHour,
                  mode: 'lines',
                  line: { color: 'black', width: 1 },
                },
              ]}
              xaxis={{ title: { text: 'UTC' } }}
              yaxis={{ title: { text: 'SFU' } }}
              downloadFilename={downloadFilename}
            />
          ) : (
            <Linechart
              key='Linechart-f107-day'
              data={[
                {
                  x: dataXDaily,
                  y: dataYDaily,
                  mode: 'lines',
                  line: { color: 'black', width: 1 },
                },
              ]}
              xaxis={{ tickformat: '%d-%m-%Y', hoverformat: '%d-%m-%Y' }}
              yaxis={{ title: { text: 'SFU' } }}
              downloadFilename={downloadFilename}
            />
          )}

          <div className='button_group'>
            <Button
              className={isHourly ? 'active_button' : 'inactive_button'}
              onClick={() => setIsHourly(true)}
              size='small'
              data-testid='solar_hourly_f10'
            >
              Hourly
            </Button>
            <Button
              className={!isHourly ? 'active_button' : 'inactive_button'}
              onClick={() => setIsHourly(false)}
              size='small'
              data-testid='solar_daily_f10'
            >
              Daily
            </Button>
          </div>

          <Divider />

          <div style={{ textAlign: 'justify' }}>
            <div style={{ marginBottom: 10 }}>
              These plots display the averaged solar radio flux for the F10.7 (≈&nbsp;3&nbsp;GHz) and F30
              (≈&nbsp;1&nbsp;GHz) indices.
            </div>

            <Header as='h4' style={{ marginTop: '0.5em' }}>
              Display modes
            </Header>
            <List bulleted>
              <List.Item>Hourly — last hour of observations, one-minute sampling per data point.</List.Item>
              <List.Item>Daily — daily averaged flux values over the past 40 days.</List.Item>
            </List>

            <div style={{ marginTop: 10 }}>
              The chart presents the variation of measured flux (<strong>y-axis</strong>) as a function of time (
              <strong>x-axis</strong>), enabling continuous monitoring of solar radio activity. Flux values are given in
              solar flux units (SFU), where 1&nbsp;SFU&nbsp;=&nbsp;10<sup>−22</sup>&nbsp;Wm<sup>−2</sup>Hz<sup>−1</sup>{' '}
              (SI units).
            </div>
          </div>
        </div>
      ),
    },
    {
      menuItem: 'F30',
      render: () => (
        <div>
          {isHourly ? (
            <Linechart
              key='Linechart-f30-hour'
              data={[
                {
                  x: dataXHour,
                  y: dataYHour,
                  mode: 'lines',
                  line: { color: 'black', width: 1 },
                },
              ]}
              xaxis={{ title: { text: 'UTC' } }}
              yaxis={{ title: { text: 'SFU' } }}
              downloadFilename={downloadFilename}
            />
          ) : (
            <Linechart
              key='Linechart-f30-day'
              data={[
                {
                  x: dataXDaily,
                  y: dataYDaily,
                  mode: 'lines',
                  line: { color: 'black', width: 1 },
                },
              ]}
              xaxis={{ tickformat: '%d-%m-%Y', hoverformat: '%d-%m-%Y' }}
              yaxis={{ title: { text: 'SFU' } }}
              downloadFilename={downloadFilename}
            />
          )}

          <div className='button_group'>
            <Button
              className={isHourly ? 'active_button' : 'inactive_button'}
              onClick={() => setIsHourly(true)}
              size='small'
              data-testid='solar_hourly_f30'
            >
              Hourly
            </Button>
            <Button
              className={!isHourly ? 'active_button' : 'inactive_button'}
              onClick={() => setIsHourly(false)}
              size='small'
              data-testid='solar_daily_f30'
            >
              Daily
            </Button>
          </div>

          <Divider />

          <div style={{ textAlign: 'justify' }}>
            <div style={{ marginBottom: 10 }}>
              These plots display the averaged solar radio flux for the F10.7 (≈&nbsp;3&nbsp;GHz) and F30
              (≈&nbsp;1&nbsp;GHz) indices.
            </div>

            <Header as='h4' style={{ marginTop: '0.5em' }}>
              Display modes
            </Header>
            <List bulleted>
              <List.Item>Hourly — last hour of observations, one-minute sampling per data point.</List.Item>
              <List.Item>Daily — daily averaged flux values over the past 40 days.</List.Item>
            </List>

            <div style={{ marginTop: 10 }}>
              The chart presents the variation of measured flux (<strong>y-axis</strong>) as a function of time (
              <strong>x-axis</strong>), enabling continuous monitoring of solar radio activity. Flux values are given in
              solar flux units (SFU), where 1&nbsp;SFU&nbsp;=&nbsp;10<sup>−22</sup>&nbsp;Wm<sup>−2</sup>Hz<sup>−1</sup>{' '}
              (SI units).
            </div>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className='fade-in'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Header as='h1' style={{ marginBottom: 0 }}>
          Solar indices
        </Header>
        {isHourly && isTechnicalDay && <DataStatusIndicator label='Flux' status='technical-day' />}
      </div>
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        id='solar_indices_tab'
        className='secondary_tab'
        activeIndex={activeTab} // poprawka: właściwe API Semantic UI
        onTabChange={(_, { activeIndex }) => {
          setActiveTab((activeIndex as number) ?? 0)
          setDataXHour([])
          setDataYHour([])
          setDataXDaily([])
          setDataYDaily([])
        }}
      />
    </div>
  )
}

export default SolarIndices
